import React, { useEffect, useState } from 'react';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';
import ListCard from '../components/ListCard';


/**
 * Admin "All Lists"
 * - Prefer listName over name so newly approved lists show proper titles.
 * - Wrapped in scroll-shell / scroll-body to restore the page scrollbar.
 */

const toArray = (d) =>
  Array.isArray(d)
    ? d
    : (d?.results || d?.lists || d?.data || d?.rankings || []);

const toImageUrl = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) return raw;
  const s = String(raw);
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${PUBLIC_BASE}${path}`;
};

const pick = (o, keys, fallback = undefined) => {
  for (const k of keys) {
    const v = o?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return fallback;
};

const normalizeRow = (row) => {
  const id = pick(row, ['_id', 'id']);
  const title = pick(row, ['listName', 'name', 'title'], id || 'Untitled');

  // creator username if available
  const createdByObj =
    (typeof row.createdBy === 'object' && row.createdBy) ||
    (typeof row.owner === 'object' && row.owner) ||
    null;

  const userFromObj =
    (createdByObj && (createdByObj.username || createdByObj.email || createdByObj.name)) || '';

  const rawSubtitle =
    userFromObj ||
    pick(row, ['username', 'author', 'ownerName', 'createdBy', 'owner']) ||
    '';

  // hide bare 24-hex ObjectIds as subtitles
  const HEX24 = /^[0-9a-f]{24}$/i;
  const subtitle =
    typeof rawSubtitle === 'string' && HEX24.test(rawSubtitle) ? '' : rawSubtitle;

  const rawImg =
    pick(row, ['image', 'cover', 'imageUrl', 'imageURL', 'thumbnail', 'imagePath', 'img']) ||
    pick(row?.image || row?.cover, ['url', 'path']);

  const imageUrl = toImageUrl(rawImg);

  return {
    id: id || title,
    title,
    subtitle: typeof subtitle === 'string' && subtitle
      ? (subtitle.startsWith('@') ? subtitle : `@${subtitle}`)
      : '',
    imageUrl,
  };
};

const ENDPOINTS = ['/rankings', '/lists', '/admin/lists'];

const AdminLists = () => {
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [err, setErr]       = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr('');
        setLoad(true);

        // Try endpoints in order; first non-empty wins
        let pool = [];
        for (const url of ENDPOINTS) {
          try {
            const r = await axios.get(url);
            const rows = toArray(r.data);
            if (Array.isArray(rows)) {
              pool = rows;
              if (rows.length > 0) break;
            }
          } catch {
            // try next
          }
        }

        const normalized = pool.map(normalizeRow);
        const seen = new Set();
        const deduped = [];
        for (const it of normalized) {
          if (seen.has(it.id)) continue;
          seen.add(it.id);
          deduped.push(it);
        }

        if (alive) setItems(deduped);
      } catch (e) {
        if (alive) setErr('Failed to load lists.');
      } finally {
        if (alive) setLoad(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="scroll-shell">
      <div className="scroll-body">
        <h2 style={{ marginBottom: 12 }}>All Lists</h2>

        {err && <div style={{ color:'#b91c1c', marginBottom:12 }}>{err}</div>}

        {loading ? (
          <p style={{ color:'#666' }}>Loading…</p>
        ) : (
          <div className="grid">
            {items.map((it) => (
              <ListCard
                key={it.id}
                id={it.id}
                // Pass title redundantly to avoid any legacy fallback to _id
                title={it.title}
                name={it.title}
                listName={it.title}
                subtitle={it.subtitle}
                imageUrl={it.imageUrl}
                to={`/admin/lists/${it.id}`}
              />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && !err && (
          <div style={{ textAlign:'center', color:'#777', marginTop:24 }}>
            No lists yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLists;
